import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../lib/api';
import {
  FaLightbulb,
  FaMicrophone,
  FaPaperPlane,
  FaStop,
  FaVolumeUp,
} from 'react-icons/fa';
import './CSS/PampaBot.css';

const QUICK_PROMPTS = [
  'Show customer credit today',
  'Show reconciliation this month',
  'I want support, passbook is not loading',
  'How does reconciliation work?',
];

const LANGUAGES = [
  { label: 'English', value: 'en-IN' },
  { label: 'Hindi', value: 'hi-IN' },
  { label: 'Tamil', value: 'ta-IN' },
  { label: 'Telugu', value: 'te-IN' },
  { label: 'Kannada', value: 'kn-IN' },
  { label: 'Malayalam', value: 'ml-IN' },
  { label: 'Marathi', value: 'mr-IN' },
  { label: 'Gujarati', value: 'gu-IN' },
  { label: 'Bengali', value: 'bn-IN' },
];

function readJsonLS(key, fallback) {
  const raw = localStorage.getItem(key);
  if (raw == null) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function formatValue(value) {
  if (typeof value === 'number') {
    return value.toLocaleString('en-IN', { maximumFractionDigits: 3 });
  }
  return String(value ?? '-');
}

export default function PampaBot() {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: 'Hi, I am PAMPA. Ask me about reports, support, or anything else and I will try to help.',
      meta: { type: 'welcome' },
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedLang, setSelectedLang] = useState('en-IN');
  const [speakReplies, setSpeakReplies] = useState(true);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);

  const token = localStorage.getItem('token');
  const name = useMemo(() => {
    const raw = readJsonLS('name', 'User');
    return String(raw || 'User');
  }, []);
  const bunkName = useMemo(() => {
    const raw = readJsonLS('bunkname', 'Fuel Station');
    return String(raw || 'Fuel Station');
  }, []);

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  };

  const showStarterPrompts = messages.length <= 1 && !loading;

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition || null;

    if (!SpeechRecognition) return undefined;

    const rec = new SpeechRecognition();
    rec.interimResults = true;
    rec.continuous = false;
    rec.lang = selectedLang;

    rec.onresult = (event) => {
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        transcript += event.results[i][0].transcript;
      }
      setInput(transcript.trim());
    };

    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;

    return () => {
      try {
        rec.stop();
      } catch {
        // ignore
      }
    };
  }, [selectedLang]);

  useEffect(() => {
    if (!recognitionRef.current) return;
    recognitionRef.current.lang = selectedLang;
  }, [selectedLang]);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    setSpeaking(false);
  };

  useEffect(() => () => stopAudio(), []);

  const speak = async (text, langHint = selectedLang) => {
    if (!speakReplies) return;
    try {
      setSpeaking(true);
      stopAudio();

      const res = await api.post(
        '/api/pampa/speak',
        { text, locale: langHint },
        { headers, responseType: 'blob' }
      );

      const blob = new Blob([res.data], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        URL.revokeObjectURL(url);
        setSpeaking(false);
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        setSpeaking(false);
      };
      await audio.play();
    } catch (err) {
      setSpeaking(false);
      setError(err?.response?.data?.error || 'Could not play PAMPA voice right now.');
    }
  };

  const sendMessage = async (messageText = input) => {
    const text = String(messageText || '').trim();
    if (!text || loading) return;

    setError('');
    setLoading(true);
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text }]);

    try {
      const res = await api.post(
        '/api/pampa/chat',
        {
          message: text,
          locale: selectedLang,
        },
        { headers }
      );

      const payload = res.data || {};
      const reply = payload.reply || 'I have processed your request.';
      const responseLocale = payload.responseLocale || selectedLang;
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: reply,
          meta: {
            mode: payload.mode,
            topic: payload.topic,
            rangeLabel: payload.rangeLabel,
            cards: payload.cards || [],
            table: payload.table || null,
            suggestions: payload.suggestions || [],
          },
        },
      ]);

      await speak(reply, responseLocale);
    } catch (err) {
      const errText =
        err?.response?.data?.error || 'PAMPA could not answer right now. Please try again.';
      setMessages((prev) => [...prev, { role: 'assistant', text: errText, meta: { type: 'error' } }]);
      setError(errText);
    } finally {
      setLoading(false);
    }
  };

  const createSupportTicket = async () => {
    const text = String(input || '').trim();
    if (!text || loading) {
      setError('Please describe the issue before creating a support ticket.');
      return;
    }

    setError('');
    setLoading(true);
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text }]);

    try {
      const res = await api.post(
        '/api/support-tickets',
        {
          title: text.slice(0, 120),
          description: text,
          priority: 'high',
          source: 'bot',
          channel: listening ? 'voice' : 'web',
        },
        { headers }
      );

      const ticketNo = res.data?.ticket_number || 'support case';
      const reply = `Done. I created ${ticketNo} and sent it to the support queue.`;
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: reply,
          meta: {
            cards: [
              { label: 'Ticket No', value: ticketNo },
              { label: 'Status', value: 'open' },
              { label: 'Priority', value: 'high' },
            ],
          },
        },
      ]);

      await speak(reply, selectedLang);
    } catch (err) {
      const errText =
        err?.response?.data?.error || 'Could not create the support ticket right now.';
      setMessages((prev) => [...prev, { role: 'assistant', text: errText, meta: { type: 'error' } }]);
      setError(errText);
    } finally {
      setLoading(false);
    }
  };

  const startListening = () => {
    if (!recognitionRef.current) {
      setError('Speech recognition is not supported in this browser.');
      return;
    }

    try {
      setError('');
      setListening(true);
      recognitionRef.current.lang = selectedLang;
      recognitionRef.current.start();
    } catch (err) {
      setListening(false);
      setError('Could not start voice recognition. Please try again.');
    }
  };

  const stopListening = () => {
    try {
      recognitionRef.current?.stop();
    } catch {
      // ignore
    }
    setListening(false);
  };

  return (
    <div className="pampa-page admin-premium-page">
      <section className="premium-hero pampa-hero">
        <div className="premium-hero-copy">
          <span className="premium-eyebrow">AI Assistant</span>
          <h1>PAMPA FuelFlow Bot</h1>
          <p>
            Ask in text or voice and PAMPA will answer in simple language with optional spoken replies.
          </p>
        </div>

        <div className="premium-hero-actions">
          <label className="pampa-field">
            <span>Language</span>
            <select
              className="form-control"
              value={selectedLang}
              onChange={(e) => setSelectedLang(e.target.value)}
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
          </label>

          <label className="pampa-switch">
            <input
              type="checkbox"
              checked={speakReplies}
              onChange={(e) => setSpeakReplies(e.target.checked)}
            />
            <span>Speak replies</span>
          </label>
        </div>
      </section>

      <section className="pampa-context">
        <div className="premium-card pampa-context-card">
          <div className="pampa-context-head">
            <div>
              <span className="premium-eyebrow">Quick prompts</span>
              <h3>Start with a question</h3>
            </div>
            <FaLightbulb aria-hidden="true" />
          </div>
          {showStarterPrompts ? (
            <div className="pampa-chip-row">
              {QUICK_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  className="pampa-chip"
                  onClick={() => sendMessage(prompt)}
                  disabled={loading}
                >
                  {prompt}
                </button>
              ))}
            </div>
          ) : (
            <p className="pampa-muted">Reply space stays clean once the conversation starts.</p>
          )}
        </div>

          <div className="premium-card pampa-context-card">
            <div className="pampa-context-head">
              <div>
                <span className="premium-eyebrow">Tenant</span>
                <h3>{bunkName}</h3>
              </div>
            </div>
            <p className="pampa-muted">
              {name} is connected. PAMPA answers in plain language and can speak replies when voice mode is on.
            </p>
          </div>
      </section>

      {error ? <div className="pampa-error">{error}</div> : null}

      <section className="pampa-main">
        <div className="premium-card pampa-chat-card">
          <div className="pampa-chat-head">
            <div>
              <span className="premium-eyebrow">Conversation</span>
              <h3>Talk to PAMPA</h3>
            </div>
          </div>

          <div className="pampa-chat-window" role="log" aria-live="polite">
            {messages.map((message, idx) => (
              <article key={`${message.role}-${idx}`} className={`pampa-bubble ${message.role}`}>
                <div className="pampa-bubble-badge">
                  {message.role === 'user' ? 'You' : 'PAMPA'}
                </div>
                <p>{message.text}</p>

                {message.role === 'assistant' && message.meta?.cards?.length ? (
                  <div className="pampa-cards-grid">
                    {message.meta.cards.map((card) => (
                      <div key={card.label} className="pampa-stat-card">
                        <span>{card.label}</span>
                        <strong>{formatValue(card.value)}</strong>
                      </div>
                    ))}
                  </div>
                ) : null}

                {message.role === 'assistant' && message.meta?.table?.rows?.length ? (
                  <div className="pampa-table-wrap">
                    <table className="table table-dark table-striped align-middle">
                      <thead>
                        <tr>
                          {message.meta.table.columns.map((col) => (
                            <th key={col}>{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {message.meta.table.rows.map((row, rowIndex) => (
                          <tr key={`row-${rowIndex}`}>
                            {message.meta.table.columns.map((col) => (
                              <td key={`${rowIndex}-${col}`}>{row[col] ?? '-'}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}

              </article>
            ))}

            {loading ? (
              <div className="pampa-bubble assistant">
                <div className="pampa-bubble-badge">PAMPA</div>
                <p>Thinking through the tenant data and support flow...</p>
              </div>
            ) : null}
            <div ref={bottomRef} />
          </div>

          <form
            className="pampa-compose"
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage();
            }}
          >
            <textarea
              className="form-control pampa-input"
              rows={3}
              placeholder="Ask PAMPA about reports or describe a support issue..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
            <div className="pampa-compose-actions">
              <span className={`pampa-voice-state ${listening ? 'active' : ''}`}>
                {listening ? 'Listening...' : speaking ? 'Speaking...' : 'Ready'}
              </span>
              <div className="pampa-send-group">
                <button
                  type="button"
                  className={`pampa-voice-btn ${listening ? 'active' : ''}`}
                  onClick={listening ? stopListening : startListening}
                  aria-pressed={listening}
                >
                  {listening ? <FaStop aria-hidden="true" /> : <FaMicrophone aria-hidden="true" />}
                  <span>{listening ? 'Stop' : 'Voice'}</span>
                </button>
                <button
                  type="button"
                  className="pampa-support-btn"
                  onClick={createSupportTicket}
                  disabled={loading || !input.trim()}
                >
                  <span>Support Ticket</span>
                </button>
                <button type="submit" className="btn btn-primary pampa-send" disabled={loading || !input.trim()}>
                  <FaPaperPlane aria-hidden="true" />
                  <span>Send</span>
                </button>
              </div>
            </div>
          </form>
        </div>

        <aside className="pampa-side">
          <div className="premium-card pampa-side-card">
            <div className="pampa-side-head">
              <span className="premium-eyebrow">Examples</span>
            </div>
            <ul className="pampa-example-list">
              <li>Show customer credit today</li>
              <li>How does reconciliation work?</li>
              <li>I want support, lube report is not loading</li>
              <li>Show expenses this month</li>
            </ul>
          </div>

          <div className="premium-card pampa-side-card">
            <div className="pampa-side-head">
              <span className="premium-eyebrow">Notes</span>
              <FaVolumeUp aria-hidden="true" />
            </div>
            <p className="pampa-muted">
              PAMPA starts as a safe, tenant-aware assistant. Report summaries and support answers are live
              first, and the same backend can later grow into a richer assistant.
            </p>
          </div>
        </aside>
      </section>
    </div>
  );
}
